import { PERMANENT_STORAGE_KEY } from '../components/utils';

const STORED_TOKEN_NAMES = {
  ACCESS_TOKEN: 'accessToken',
  ACCESS_EXPIRES_ON: 'accessExpiresOn',
  REFRESH_TOKEN: 'refreshToken',
  TOKEN_TYPE: 'tokenType',
  SCOPE: 'scope',
  ACCESS_TOKEN_EXPIRES_AT: 'accessTokenExpiresAt',
  REFRESH_TOKEN_EXPIRES_AT: 'refreshTokenExpiresAt'
}; // check values in tokenize function (requestAgent.js)

export const LAST_USER_ACTIVITY_DATE_TIME = 'lastUserActivityDateTime';

export const LAST_SELECTED_ACCOUNT_NUMBER = 'lastSelectedAccountNumber';

export const removeAccessToken = () => window.localStorage.removeItem(STORED_TOKEN_NAMES.ACCESS_TOKEN);

export const getAccessToken = () => window.localStorage.getItem(STORED_TOKEN_NAMES.ACCESS_TOKEN);

export const getRefreshToken = () => window.localStorage.getItem(STORED_TOKEN_NAMES.REFRESH_TOKEN);

export const getAccessExpiresOn = () => window.localStorage.getItem(STORED_TOKEN_NAMES.ACCESS_EXPIRES_ON);

export const getTokenType = () => window.localStorage.getItem(STORED_TOKEN_NAMES.TOKEN_TYPE);

export const getScope = () => window.localStorage.getItem(STORED_TOKEN_NAMES.SCOPE);

export const getAccessTokenExpiresAt = () => window.localStorage.getItem(STORED_TOKEN_NAMES.ACCESS_TOKEN_EXPIRES_AT);

export const getRefreshTokenExpiresAt = () => window.localStorage.getItem(STORED_TOKEN_NAMES.REFRESH_TOKEN_EXPIRES_AT);

// Drops only the stored OAuth tokens. Used when the user cancels the 2FA step: the password step has already
// saved a token that isn't fully authorized yet, and leaving it would make App.js treat the visitor as logged in
// on the next route (and bounce them back to /login after a 401). Unlike clearLocalStorage() this keeps everything
// else — UTM tags for the registration, the resend-code timer, etc.
export const clearStoredTokens = () =>
  Object.values(STORED_TOKEN_NAMES).forEach((name) => window.localStorage.removeItem(name));

export const clearLocalStorage = () => {
  const permanentStorage = window.localStorage.getItem(PERMANENT_STORAGE_KEY);
  window.localStorage.clear();
  window.localStorage.setItem(PERMANENT_STORAGE_KEY, permanentStorage);
};

export const isTokenValid = () => {
  const accessToken = getAccessToken();
  const refreshToken = getRefreshToken();
  const accessExpiresOn = getAccessExpiresOn();
  const scope = getScope();

  // Check if we have a 2fa scope (limited access) or full access
  const hasValidScope = scope === 'all' || scope === '2fa';

  return accessToken && refreshToken && accessExpiresOn > Date.now() && hasValidScope;
};

export const setConfirmationActionType = (type) => localStorage.setItem('CONFIRMATION_TYPE', type);
export const setAvailableConfirmationTypes = (types) => {
  if (Array.isArray(types)) {
    localStorage.setItem('AVAILABLE_CONFIRMATION_TYPES', JSON.stringify(types));
  } else {
    localStorage.setItem('AVAILABLE_CONFIRMATION_TYPES', types);
  }
};
export const setTimerDurationExpiresAt = (expiresAt) => localStorage.setItem('TIMER_DURATION_EXPIRES_AT', expiresAt);

export const getConfirmationActionType = () => localStorage.getItem('CONFIRMATION_TYPE');
export const getAvailableConfirmationTypes = () => {
  const types = localStorage.getItem('AVAILABLE_CONFIRMATION_TYPES');
  try {
    return JSON.parse(types) || [];
  } catch {
    return types || [];
  }
};
export const getTimerDurationExpiresAt = () => localStorage.getItem('TIMER_DURATION_EXPIRES_AT');

export const removeConfirmationActionType = () => localStorage.removeItem('CONFIRMATION_TYPE');

export const updateConfirmationActionType = (confirmationType) => {
  const currentConfirmationType = getConfirmationActionType();
  if (!currentConfirmationType || currentConfirmationType !== confirmationType) {
    localStorage.setItem('CONFIRMATION_TYPE', confirmationType);
  }
};

export const setSecureUserPhone = (phone) => localStorage.setItem('SECURE_PHONE', phone);

export const getSecureUserPhone = () => localStorage.getItem('SECURE_PHONE');

export const setSecureUserEmail = (email) => localStorage.setItem('SECURE_EMAIL', email);

export const getSecureUserEmail = () => localStorage.getItem('SECURE_EMAIL');

export const getMaskedEmail = (email) => {
  if (!email || !email.includes('@')) return email;
  const [local, domain] = email.split('@');
  const maskPart = (part) =>
    part.length <= 2 ? `${part[0]}*` : `${part[0]}${'*'.repeat(Math.min(part.length - 2, 6))}${part[part.length - 1]}`;
  const dotIndex = domain.lastIndexOf('.');
  const domainName = dotIndex > 0 ? domain.slice(0, dotIndex) : domain;
  const tld = dotIndex > 0 ? domain.slice(dotIndex) : '';
  return `${maskPart(local)}@${maskPart(domainName)}${tld}`;
};

// TRN-7254: 2FA modals show this next to the destination (e.g. "sent to +38********76"), same as
// getMaskedEmail above — keep the leading "+countrycode" (or first 3 digits if there's no "+")
// and the last 2 digits visible, mask the rest (capped like getMaskedEmail so a long number
// doesn't produce a wall of asterisks).
export const getMaskedPhone = (phone) => {
  if (!phone) return phone;
  const visibleStart = phone.startsWith('+') ? 4 : 3;
  const visibleEnd = 2;
  if (phone.length <= visibleStart + visibleEnd) return phone;
  const maskLength = Math.min(phone.length - visibleStart - visibleEnd, 6);
  return `${phone.slice(0, visibleStart)}${'*'.repeat(maskLength)}${phone.slice(-visibleEnd)}`;
};

// Destination the confirmation code was sent to, depending on the confirmation type
export const getConfirmationDestination = (type) =>
  type === 'EMAIL' ? getMaskedEmail(getSecureUserEmail()) : getMaskedPhone(getSecureUserPhone());

export const saveTokenInfo = (info) => {
  // Save all token-related information including new OAuth2 fields
  Object.entries(info).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      window.localStorage.setItem(key, value);
    }
  });

  // Save scope if provided
  if (info.scope) {
    window.localStorage.setItem(STORED_TOKEN_NAMES.SCOPE, info.scope);
  }

  // Save expiration timestamps if provided
  if (info.accessTokenExpiresAt) {
    window.localStorage.setItem(STORED_TOKEN_NAMES.ACCESS_TOKEN_EXPIRES_AT, info.accessTokenExpiresAt);
  }

  if (info.refreshTokenExpiresAt) {
    window.localStorage.setItem(STORED_TOKEN_NAMES.REFRESH_TOKEN_EXPIRES_AT, info.refreshTokenExpiresAt);
  }
};

export const setLastUserActivityDateTime = () => localStorage.setItem(LAST_USER_ACTIVITY_DATE_TIME, new Date());

export const resetLastUserActivityDateTime = () => localStorage.setItem(LAST_USER_ACTIVITY_DATE_TIME, '');

export const getLastUserActivityDateTime = () => localStorage.getItem(LAST_USER_ACTIVITY_DATE_TIME);

export const setSelectedMultiAccount = (accountNumber) =>
  localStorage.setItem(LAST_SELECTED_ACCOUNT_NUMBER, accountNumber);

export const getSelectedMultiAccount = () => localStorage.getItem(LAST_SELECTED_ACCOUNT_NUMBER);

export const logoutActiveSessionWithoutToken = () => {
  window.location = '/logout';
  clearLocalStorage();
};

export const isActiveSession = () =>
  // eslint-disable-next-line max-len
  new Date() - new Date(getLastUserActivityDateTime()) < process.env.REACT_APP_PROLONGATION_MODAL_APPEARING_TIME * 1000;

export const USER_ID_INTERNAL_LOCAL_STORAGE = 'user_id_internal';
export const CUSTOMER_ID_LOCAL_STORAGE = 'customer_id';
export const CUSTOMER_TYPE_LOCAL_STORAGE = 'customer_type';

export const setIsSuccessLoginStatus = (status) => localStorage.setItem('isSuccessLogin', status);
export const setInternalUserId = (id) => localStorage.setItem(USER_ID_INTERNAL_LOCAL_STORAGE, id);
export const setCustomerId = (id) => localStorage.setItem(CUSTOMER_ID_LOCAL_STORAGE, id);
export const setCustomerType = (type) => localStorage.setItem(CUSTOMER_TYPE_LOCAL_STORAGE, type);

export const getIsSuccessLoginStatus = () => localStorage.getItem('isSuccessLogin');
export const getInternalUserId = () => localStorage.getItem(USER_ID_INTERNAL_LOCAL_STORAGE);
export const getCustomerId = () => localStorage.getItem(CUSTOMER_ID_LOCAL_STORAGE);
export const getCustomerType = () => localStorage.getItem(CUSTOMER_TYPE_LOCAL_STORAGE);

export const removeIsSuccessLoginStatus = () => localStorage.removeItem('isSuccessLogin');

export const getCookie = (name) => {
  const matches = document.cookie.match(
    new RegExp(`(?:^|; )${name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1')}=([^;]*)`)
  );
  return matches ? decodeURIComponent(matches[1]) : undefined;
};

export const setCookie = (name, value, options = {}) => {
  options = {
    path: '/',
    // Default expiration to 1 year
    ...options
  };

  if (options.expires instanceof Date) {
    options.expires = options.expires.toUTCString();
  }

  let updatedCookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

  for (const optionKey in options) {
    updatedCookie += `; ${optionKey}`;
    const optionValue = options[optionKey];
    if (optionValue !== true) {
      updatedCookie += `=${optionValue}`;
    }
  }

  document.cookie = updatedCookie;
};
